import { useMemo } from 'react';

import { Badge, Divider, Popover } from 'antd';
import {
  Avatar,
  AvatarShape,
  Column,
  Icon,
  Row,
  Space,
  Text,
  Tooltip,
  TooltipPlacement,
} from 'farmhub-ui-core';
import { useHistory } from 'react-router';

import { useAppSelector } from 'app';
import { EditableTagGroup, ViewTagGroup } from 'components';
import { ConnectionIcon } from 'components/Icon';
import { selectProductUnit } from 'slice';
import { theme } from 'styled';
import { IMAGETYPE, SIZE } from 'utils/constants';
import {
  formatImageUrl,
  genWholesaleUnit,
  getEntityInfo,
  handleRedirect,
} from 'utils/function';

import {
  AvatarStyles,
  DivInfo,
  EntityTypeIconStyles,
  Img,
  ImgWrapper,
  Wrapper,
} from './styles';

type ownerType = {
  avatar: string;
  name: string;
};

interface InfoCardProps {
  id?: string;
  name: string | React.ReactNode;
  img?: string | React.ReactNode;
  nameString?: string;
  info?: string | React.ReactNode;
  subInfo?: React.ReactNode;
  entityType?: number;
  link?: string;
  redirectLink?: string;
  width?: number | string;
  tags?: Array<any>;
  isReadOnlyTabs?: boolean;
  editAbleTag?: boolean;
  imageType?: 'PRODUCT' | 'CATEGORY';
  owner?: ownerType;
  showConnectionProduct?: boolean;
  showCustomContent?: boolean;
  popoverCustomerContent?: React.ReactNode;
  customContentName?: React.ReactNode;
  connectionProduct?: any;
  avatarCircle?: boolean;
  size?: SIZE;
  sizeSpaceWithInfo?: number;
  showDot?: boolean;
  optionType?: 'PRODUCT' | 'ORDER';
  wrapperStyles?: any;
  onChangeOptions?: (
    options: any[],
    deletedOptionIDs?: string[],
    productOptions?: any[]
  ) => void;
  onClickOwner?: () => void;
}

export const InfoCardSKUUnit = ({
  code,
  unitPrefix,
  unitWeight,
  unitPostfix,
  textSize = 'xxs',
  lineNumber = 2,
}: {
  code: string;
  unitPrefix: number;
  unitWeight: number;
  unitPostfix: number;
  textSize?: 'small' | 'tiny' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  lineNumber?: number;
}): JSX.Element => {
  const productUnit = useAppSelector(selectProductUnit);
  const content = useMemo(
    () => (
      <Row>
        <Text
          color="main"
          lineHeight="22px"
          lineNumber={lineNumber}
          size={textSize}
        >
          {code}
        </Text>
        <Space width={8} />
        <Divider
          style={{ color: theme.colors.border.main, margin: 0 }}
          type="vertical"
        />
        <Space width={8} />
        <Text
          color="main"
          lineHeight="22px"
          lineNumber={lineNumber}
          size={textSize}
        >
          {genWholesaleUnit({
            unitPrefix: productUnit.prefixObj[unitPrefix],
            unitWeight,
            unitPostfix: productUnit.postfixObj[unitPostfix],
          })}
        </Text>
      </Row>
    ),
    [code, lineNumber, textSize, unitPostfix, unitPrefix, unitWeight]
  );

  const tooltipTitle = useMemo(
    () =>
      `SKU: ${code} Đơn vị: ${genWholesaleUnit({
        unitPrefix: productUnit.prefixObj[unitPrefix],
        unitWeight,
        unitPostfix: productUnit.postfixObj[unitPostfix],
      })}`,
    [code, unitPostfix, unitPrefix, unitWeight]
  );

  return (
    <>
      {lineNumber === 1 ? (
        <Tooltip placement={TooltipPlacement.Top} title={tooltipTitle}>
          <Text
            color="main"
            lineHeight="22px"
            lineNumber={lineNumber}
            size={textSize}
          >
            {content}
          </Text>
        </Tooltip>
      ) : (
        <Text
          color="main"
          lineHeight="22px"
          lineNumber={lineNumber}
          size={textSize}
        >
          {content}
        </Text>
      )}
    </>
  );
};

const InfoCard = ({
  id,
  name,
  nameString,
  img,
  entityType,
  info,
  link,
  redirectLink,
  width,
  tags,
  isReadOnlyTabs,
  editAbleTag,
  imageType,
  owner,
  subInfo,
  showConnectionProduct,
  showCustomContent,
  popoverCustomerContent,
  customContentName,
  connectionProduct,
  avatarCircle = false,
  size = SIZE.DEFAULT,
  sizeSpaceWithInfo,
  showDot = false,
  optionType = 'PRODUCT',
  wrapperStyles,
  onChangeOptions,
  onClickOwner,
}: InfoCardProps): JSX.Element => {
  const history = useHistory();

  const content = (
    <div>
      <Row>
        <ImgWrapper>
          <Img
            alt="img"
            entityType={entityType}
            objectFit="cover"
            size={
              size === SIZE.SMALL ? theme.avatar.size.sm : theme.avatar.size.md
            }
            src={formatImageUrl(
              connectionProduct?.avatar,
              IMAGETYPE.AVATAR.xxs
            )}
          />
        </ImgWrapper>
        <DivInfo>
          <Column>
            <Row alignItems="flex-start">
              <Text
                lineHeight="22px"
                lineNumber={1}
                title={connectionProduct?.name}
              >
                {connectionProduct?.name}
              </Text>
              <Space width={6} />
            </Row>
            <Text color="main" lineHeight="22px" lineNumber={2} size="xxs">
              {connectionProduct?.code}
            </Text>
          </Column>
        </DivInfo>
      </Row>
    </div>
  );

  const GenImage = () => {
    if (img) {
      if (typeof img === 'object') {
        return img;
      }
      return (
        <Badge dot={showDot}>
          <ImgWrapper>
            {avatarCircle ? (
              <Avatar
                shape={AvatarShape.Circle}
                size={
                  size === SIZE.SMALL
                    ? theme.avatar.size.sm
                    : theme.avatar.size.md
                }
                src={formatImageUrl(String(img), IMAGETYPE.AVATAR.xxs)}
              />
            ) : (
              <Img
                alt="img"
                entityType={entityType}
                objectFit={imageType === 'CATEGORY' ? 'contain' : 'cover'}
                size={
                  size === SIZE.SMALL
                    ? theme.avatar.size.sm
                    : theme.avatar.size.md
                }
                src={formatImageUrl(String(img), IMAGETYPE.AVATAR.xxs)}
              />
            )}
            {(entityType === 0 || entityType) && (
              <Tooltip
                placement={TooltipPlacement.Top}
                title={getEntityInfo(entityType).name}
              >
                <Icon height="18px" styles={EntityTypeIconStyles} width="18px">
                  {getEntityInfo(entityType).icon}
                </Icon>
              </Tooltip>
            )}
            {owner && (
              <Row onClick={onClickOwner}>
                <Tooltip placement={TooltipPlacement.Top} title={owner.name}>
                  <Avatar
                    size={size === SIZE.SMALL ? 16 : 18}
                    src={formatImageUrl(owner.avatar, IMAGETYPE.AVATAR.xxs)}
                    styles={AvatarStyles}
                  />
                </Tooltip>
              </Row>
            )}
            {subInfo}
          </ImgWrapper>
        </Badge>
      );
    }
    return <></>;
  };

  const GenName = () => {
    if (customContentName) {
      return customContentName;
    }
    if (name) {
      if (typeof name === 'object') {
        return name;
      }
      return (
        <Tooltip title={name}>
          <Text color="dark" lineHeight="20px" lineNumber={4} weight="medium">
            {name}
          </Text>
        </Tooltip>
      );
    }
    return (
      <Text lineHeight="22px" title="Trống" weight="medium">
        --
      </Text>
    );
  };

  const GenInfo = () => {
    if (info) {
      if (typeof info === 'object') {
        return info;
      }
      return (
        <Text color="main" lineHeight="22px" lineNumber={2} size="xxs">
          {info}
        </Text>
      );
    }
    return null;
  };

  return (
    <Wrapper
      style={wrapperStyles}
      width={width}
      onClick={() =>
        (link && history.push(link)) ||
        (redirectLink && handleRedirect(redirectLink))
      }
    >
      {GenImage()}
      {img && (
        <Space
          width={
            sizeSpaceWithInfo != undefined
              ? sizeSpaceWithInfo
              : size === 'small'
              ? 6
              : 12
          }
        />
      )}
      <Column styles={{ minHeight: size === 'small' ? 36 : 42, gap: 3 }}>
        <Row alignItems="center">
          {GenName()}
          {showConnectionProduct && (
            <>
              <Space width={6} />
              <Popover content={content} title="Sản phẩm liên kết">
                <div style={{ flexShrink: 0, width: 18, height: 18 }}>
                  <ConnectionIcon
                    color={theme.colors.primary.main}
                    height={18}
                    width={18}
                  />
                </div>
              </Popover>
            </>
          )}
          {showCustomContent ? (
            <>
              <Space width={6} />
              {popoverCustomerContent}
            </>
          ) : null}
        </Row>
        {GenInfo()}
        {/* <Space height={4} /> */}
        {tags &&
          (isReadOnlyTabs ? (
            <ViewTagGroup
              id={id}
              name={nameString}
              tags={tags.map(item => item?.option || item)}
              type={optionType}
            />
          ) : (
            <EditableTagGroup
              editAble={editAbleTag}
              id={id}
              name={nameString}
              tags={tags.map(item => item?.option || item)}
              type={optionType}
              onAfterSubmit={onChangeOptions}
            />
          ))}
      </Column>
    </Wrapper>
  );
};

export default InfoCard;
